import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import '@mobiscroll/react/dist/css/mobiscroll.min.css';
import {
  Eventcalendar,
  MbscCalendarEvent,
  MbscEventcalendarView,
  CalendarNav,
  CalendarPrev,
  CalendarNext,
  CalendarToday,
  MbscPageChangeEvent,
  MbscCalendarEventData,
} from '@mobiscroll/react';
import { DateTime } from 'luxon';

import { LocalAirport as LocalAirportIcon, HourglassFull as HourglassFullIcon, Person as PersonIcon, PriorityHigh as PriorityHighIcon, ShoppingCart as ShoppingCartIcon, NotificationsActive as NotificationsActiveIcon, Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon } from '../../components/icons';

import { Typography, IconButton, Card, CardContent, Badge, Switch, Tooltip, Tabs, Dialog, Table, TableCell, TableRow } from '../../tailwindcomponents';
import LineWorkpackageCard from './LineWorkpackageCard';
import LineWorkpackageNotes from './LineWorkpackageNotes';
import { redirectToMxi } from '../../config/mxiProvider';
import lineApi from '../../api/line';
import { LineExtraTaskInWorkpackage, LineWorkPackages, DateRanges, CalendarFlightData } from '../../types/line';
import { Event as PacketEvent } from '../../types/tasks';
import { formatDueDate, getOperationalDay } from '../../utils/dateUtils';
import { redirectToIdmrTask } from '../../config/idmrProvider';
import { isBoeingAircraft } from '../../utils/aircraftUtils';
import { palette } from '../../theme/palette';

type TimelineProps = {
  setLoading: Function;
  isAdmin: boolean;
  accessGroups: Array<string>;
  location: string;
};

type CalendarEvent = MbscCalendarEvent & {
  id: string | number;
  originalStartText?: string;
  originalEndText?: string;
  description?: string;
  packet?: PacketEvent;
};

type DayBucket = MbscCalendarEvent & {
  id: string;
  items: CalendarEvent[];
};

type WeekBar = {
  ev: CalendarEvent;
  colStart: number;
  colEnd: number;
  lane: number;
};

type WeekBucket = MbscCalendarEvent & {
  id: string;
  days: CalendarEvent[][];
  bars: WeekBar[];
  laneCount: number;
};

const MXI_FMT = 'dd.LL.yyyy HH:mm';

const iso = (dt: DateTime): string => {
  return dt.isValid ? (dt.toISO() ?? dt.toJSDate().toISOString()) : new Date().toISOString();
};

const getStatusColor = (status?: string) => {
  switch (status) {
    case 'COMPLETE': return palette.success;
    case 'ACTV': return palette.secondary;
    case 'PAUSE': return palette.status.pause;
    case 'CANCEL': return palette.error;
    case 'COMMIT': return palette.secondary;
    case 'IN WORK': return palette.primary;
    default: return palette.primary;
  }
};

const getTextColorForBg = (bg?: string) => {
  if (!bg) return palette.backgroundPaper;
  const upper = bg.toUpperCase();
  if (upper === palette.backgroundPaper) return palette.content;
  if (upper === palette.status.pause) return palette.content;
  return palette.backgroundPaper;
};

/**
 * Fill + readable text for a task status chip. Same pairing the calendar events use
 * (see the `bg` / `textColor` locals in the event renderer), so a task keeps its
 * colour when you open it instead of going grey.
 */
const getStatusChipStyle = (status?: string): React.CSSProperties => {
  const backgroundColor = getStatusColor(status);
  return { backgroundColor, color: getTextColorForBg(backgroundColor) };
};

const getPriorityBadge = (taskPriority?: string) => {
  if (!taskPriority) return '';
  if (taskPriority === 'P01') return 'P1';
  if (taskPriority === 'P02') return 'P2';
  if (taskPriority === 'P03') return 'P3';
  return '';
};

const WorkPackageCalendar: FunctionComponent<TimelineProps> = ({ setLoading, isAdmin, accessGroups, location }) => {
  const hr = new Date().getHours();
  const initialIsDay = hr >= 6 && hr <= 18;

  const [isDayNight, setToggleDayNight] = useState<boolean>(initialIsDay);
  const [calendarEvents, setCalendarEvents] = useState<PacketEvent[]>([]);
  const [activeDate, setActiveDate] = useState<Date>(() => getOperationalDay(DateTime.now()).toJSDate());
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<LineWorkPackages | undefined>(undefined);
  const [tabValue, setTabValue] = useState<number>(0);
  const [workpackageTasks, setWorkpackageTasks] = useState<LineExtraTaskInWorkpackage[]>([]);
  const [fullScreen, setFullScreen] = useState(false);

  const isLinePlanner =
    accessGroups.includes('Line Planner') ||
    accessGroups.includes('Line Planner View') ||
    accessGroups.includes('Rvk Line Edit');

  // Helper: Return first non-null date (prioritize actual > estimated > scheduled)
  const returnFirstNotNullValue = (actual?: Date | string | null, estimated?: Date | string | null, scheduled?: Date | string | null): Date | null => {
    if (actual) return new Date(actual);
    if (estimated) return new Date(estimated);
    if (scheduled) return new Date(scheduled);
    return null;
  };

  const parseMxiTextToDate = (value?: string): Date | null => {
    if (!value) return null;
    const parsed = DateTime.fromFormat(value, MXI_FMT);
    return parsed.isValid ? parsed.toJSDate() : null;
  };

  // Helper: Check if workpackage dates fall within any flight date range
  const isWithinAnyRange = (wpStart: Date, wpEnd: Date, dateRanges: DateRanges[]): boolean => {
    return dateRanges.some(range => {
      if (!range.arrival_date && !range.departure_date) return false;
      // If arrival is unknown, assume aircraft was already on the ground (open-ended past)
      const arrival = range.arrival_date ? new Date(range.arrival_date) : new Date(0);
      // If departure is unknown, assume aircraft hasn't left yet (open-ended future)
      const departure = range.departure_date ? new Date(range.departure_date) : new Date(8640000000000000);

      // Check if workpackage fits fully within the aircraft's ground time (arrival → departure)
      return (wpStart >= arrival && wpEnd <= departure)
    });
  };

  // Calculate is_on_time status for workpackages based on flight data
  const enrichWorkpackagesWithFlightData = (workpackages: PacketEvent[], flightData: CalendarFlightData[]): PacketEvent[] => {
    const now = new Date();

    return workpackages.map(wp => {
      const wpStart = parseMxiTextToDate(wp.task_details?.mxi_start_date_text) || wp.start;
      const wpEnd = parseMxiTextToDate(wp.task_details?.mxi_end_date_text) || wp.end;
      const tailNumber = wp.task_details?.tail_number;
      // Skip if no tail number or if workpackage is not in current/future timeframe
      if (!tailNumber || wpEnd < now) {
        return wp;
      }
      // Skip flight-data check for workpackages starting 6+ days in the future
      // (flight data that far out is unreliable, no need to show the icon)
      const sixDaysFromNow = new Date(now.getTime() + 6 * 24 * 60 * 60 * 1000);
      if (wpStart >= sixDaysFromNow) {
        return wp;
      }
      // Find matching flights for this aircraft
      const matchingFlights = flightData.filter(flight => {
        // Normalize tail numbers by removing hyphens (TF-FIV -> TFFIV)
        const flightTail = flight.aircraft.tailNumber?.toUpperCase().replace(/-/g, '');
        const wpTail = tailNumber.toUpperCase().replace(/-/g, '');
        return flightTail === wpTail;
      });
      if (matchingFlights.length === 0) {
        // No flights found - workpackage is NOT on time (consistent with backend logic)
        return {
          ...wp,
          task_details: {
            ...wp.task_details,
            is_on_time: false,
            aircraft_arrival_and_departure_times: wp.task_details.aircraft_arrival_and_departure_times || [],
          },
        };
      }

      // Build date ranges from flight data
      const dateRanges: DateRanges[] = matchingFlights.map(flight => {
        // Extract arrival times (may be undefined if no inbound flight data)
        const actualArrival = flight.flightInbound?.touchDownTimeUtc;
        const estimatedArrival = flight.flightInbound?.estimatedTouchDownUtc;
        const scheduledArrival = flight.flightInbound?.scheduledArrivalTimeUtc;

        // Extract departure times
        const actualDeparture = flight.takeOffTimeUtc;
        const estimatedDeparture = flight.estimatedTakeOffUtc;
        const scheduledDeparture = flight.scheduledDepartureTimeUtc;

        const arrival = returnFirstNotNullValue(actualArrival, estimatedArrival, scheduledArrival);
        const departure = returnFirstNotNullValue(actualDeparture, estimatedDeparture, scheduledDeparture);

        return {
          arrival_date: arrival,
          arrival_date_text: arrival?.toLocaleString() || '',
          departure_date: departure,
          departure_date_text: departure?.toLocaleString() || '',
        };
      }).filter(range => range.arrival_date || range.departure_date);

      const isOnTime = isWithinAnyRange(wpStart, wpEnd, dateRanges);

      // Sort date ranges by arrival_date (ascending)
      const sortedDateRanges = dateRanges.sort((a, b) => {
        if (!a.arrival_date) return 1;
        if (!b.arrival_date) return -1;
        return new Date(a.arrival_date).getTime() - new Date(b.arrival_date).getTime();
      });
      return {
        ...wp,
        task_details: {
          ...wp.task_details,
          is_on_time: isOnTime,
          aircraft_arrival_and_departure_times: sortedDateRanges.length > 0 ? sortedDateRanges : (wp.task_details.aircraft_arrival_and_departure_times || []),
        },
      };
    });
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await lineApi.GetLinePacketsForCalendar(location);
      if (res?.status !== true) return;

      const temp: PacketEvent[] = res.data.map((task: PacketEvent) => ({
        ...task,
        start: new Date(task.start),
        end: new Date(task.end),
      }));

      setCalendarEvents([...temp]); // fast first render

      const barcodes = temp
        .map(t => t.task_details?.barcode)
        .filter((b): b is string => !!b);

      const [priorityResult, flightsResult] = await Promise.allSettled([
        lineApi.GetLinePacketPriorityAndDueDateByBarcode(barcodes),
        lineApi.GetCalendarFlightsData(location),
      ]);

      let merged = [...temp];

      if (priorityResult.status === 'fulfilled' && priorityResult.value?.status === true) {
        const priorityMap = new Map(priorityResult.value.data.map(p => [p.wo_barcode, p]));
        merged = merged.map(item => {
          const pr = priorityMap.get(item.task_details?.barcode);
          return pr
            ? { ...item, task_priority: pr.task_priority, due_date: pr.due_date }
            : item;
        });
      }

      if (flightsResult.status === 'fulfilled' && flightsResult.value?.status === true && flightsResult.value.data) {
        merged = enrichWorkpackagesWithFlightData(merged, flightsResult.value.data);
      }

      setCalendarEvents(merged);
    } catch (e) {
      console.error('GetLinePacketsForCalendar failed:', e);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDayNightToggle = () => setToggleDayNight(prev => !prev);

  const parseMxi = useCallback((text?: string) => DateTime.fromFormat(text ?? '', MXI_FMT), []);

  const formatTimeOnly = useCallback((text?: string) => {
    const dt = DateTime.fromFormat(text ?? '', MXI_FMT);
    return dt.isValid ? dt.toFormat('HH:mm') : '—';
  }, []);

  // Map packets -> Mobiscroll events
  // (midnight→midnight display + keep originals)
  const mapPacketsToCalendar = useCallback(
    (packets: PacketEvent[]): CalendarEvent[] => {

      const events: CalendarEvent[] = packets.map((p, idx) => {
        const startText = p.task_details?.mxi_start_date_text || '';
        const endText = p.task_details?.mxi_end_date_text || '';

        // Parse actual start/end from MXI
        const start = parseMxi(startText);
        let end = parseMxi(endText);

        // Normalize the DISPLAYED EVENT RANGE to the operational day.
        // Events between 00:00–05:59 belong to the previous day's night shift.
        const startOfDay = getOperationalDay(start);

        // For KEF location, display workpackages on a single day only (start day)
        // For other locations, span from start operational day to end operational day
        const endOfDay = location === 'KEF'
          ? startOfDay.endOf('day')           // Same operational day as start
          : getOperationalDay(end).endOf('day');  // End operational day

        const id =
          p.scheduled_task_id && p.scheduled_task_id !== 0
            ? `wp-${p.scheduled_task_id}`
            : `wp-${idx}-${startText}-${endText}`;

        const bg = p.task_details?.color
          ? p.task_details.color
          : getStatusColor(p.task_details?.status);

        return {
          id,
          // The timeline uses these (must be strict ISO strings)
          start: iso(startOfDay),
          end: iso(endOfDay),
          title: p.title,
          color: bg,

          // Store real MXI times for sorting and display
          originalStartText: startText,
          originalEndText: endText,
          actualStart: start,   // <-- Keep real DateTime for sorting
          actualEnd: end,

          description: p.title,
          packet: p,
        };
      });

      // Correct sorting (by REAL start time, not normalized midnight)
      events.sort((a, b) => {
        const aMs = a.actualStart.toMillis();
        const bMs = b.actualStart.toMillis();
        return aMs - bMs;
      });

      return events;
    },
    [parseMxi, location]
  );

 const filteredTasks = useMemo(() => {
  return calendarEvents.filter(t => {
    if (location !== 'KEF') {
      const start = DateTime.fromFormat(t.task_details?.mxi_start_date_text || '', MXI_FMT);
      const end = DateTime.fromFormat(t.task_details?.mxi_end_date_text || '', MXI_FMT);

      if (start.isValid && end.isValid) {
        // For non-KEF, show workpackages that span multiple operational days regardless of day/night toggle
        const spansMultipleDays = !getOperationalDay(start).hasSame(getOperationalDay(end), 'day');
        if (spansMultipleDays) return true;
      }
    }

    // KEF or single-day non-KEF workpackages: filter by day/night as before
    return t.task_details?.is_day === isDayNight;
  });
}, [calendarEvents, isDayNight, location]);

  const myEvents = useMemo(() => {
    return mapPacketsToCalendar(filteredTasks);
  }, [filteredTasks, mapPacketsToCalendar]);

  const onClose = () => {
    setWorkpackageTasks([]);
    setEventDialogOpen(false);
    setTabValue(0);
  };

  const generateTableBody = (row: DateRanges, index: number) => (
    <TableRow key={index}>
      <TableCell>{index}</TableCell>
      <TableCell>{row.arrival_date_text}</TableCell>
      <TableCell>{row.departure_date_text}</TableCell>
    </TableRow>
  );

  const tabNames = isLinePlanner ? ['Task', 'Notes', 'Arrival & Departure'] : ['Task', 'Notes'];

  const openWorkpackage = useCallback(
    (realEvent: CalendarEvent) => {
      const packet = realEvent.packet;
      if (!packet) return;

      const td = packet.task_details;
      if (td?.status === 'HEADER') return;

      const lineWorkpackage: LineWorkPackages = {
        wo_number: td?.wo_number,
        wo_barcode: td?.barcode,
        status: td?.status,
        wo_name: packet.title,
        mxi_start_date_text: td?.mxi_start_date_text,
        mxi_end_date_text: td?.mxi_end_date_text,
        mxi_due_date_text: td?.due_date_text,
        maintener_planned_date_text: td?.maintener_planned_date_text,
        tail_number: td?.tail_number,
        scheduled_task_id: packet.scheduled_task_id,
        is_task_critical: td?.is_task_critical,
        is_on_hold: td?.is_on_hold,
        is_on_time: td?.is_on_time,
        package_duration_text: td?.package_duration_text,
        aircraft_arrival_and_departure_times: td?.aircraft_arrival_and_departure_times || [],
        due_date: packet.due_date,
        inventory: packet.inventory,
        is_unassigned: td?.is_unassigned,
      } as LineWorkPackages;

      setSelectedEvent(lineWorkpackage);

      if (td?.wo_number) {
        lineApi.GetTasksByLinePackageWoNumber(td.wo_number).then((res) => {
          setWorkpackageTasks(res.data ?? []);
        });
      } else {
        setWorkpackageTasks([]);
      }

      setEventDialogOpen(true);
    },
    []
  );

  // The timeline stacks events in horizontal tracks shared across the whole week, so
  // variable-height cards leave gaps and line up across columns. Instead we give
  // Mobiscroll one "bucket" event per day and stack that day's work packages inside it,
  // so each column only grows to fit its own cards.
  const dayBuckets = useMemo((): DayBucket[] => {
    const byDay = new Map<string, CalendarEvent[]>();

    myEvents.forEach(ev => {
      const end = DateTime.fromISO(ev.end as string).startOf('day');
      let cursor = DateTime.fromISO(ev.start as string).startOf('day');
      while (cursor <= end) {
        const key = cursor.toISODate() || '';
        if (!byDay.has(key)) byDay.set(key, []);
        byDay.get(key)!.push(ev);
        cursor = cursor.plus({ days: 1 });
      }
    });

    return Array.from(byDay.entries()).map(([key, items]) => {
      const day = DateTime.fromISO(key);
      return {
        id: `day-${key}`,
        start: iso(day.startOf('day')),
        end: iso(day.endOf('day')),
        items: [...items].sort((a, b) => a.actualStart.toMillis() - b.actualStart.toMillis()),
      };
    });
  }, [myEvents]);

  // Non-KEF: Mobiscroll gets one bucket per week and we lay the week out ourselves in a
  // CSS grid. Multi-day work packages are lane-packed into continuous bars at the top;
  // each day's single-day work packages then stack directly under the lowest bar in
  // that column, independent of the other columns.
  const nonKefData = useMemo((): WeekBucket[] => {
    if (location === 'KEF') return [];

    const byActualStart = (a: CalendarEvent, b: CalendarEvent) => a.actualStart.toMillis() - b.actualStart.toMillis();
    const weeks = new Map<string, { days: CalendarEvent[][]; segments: Omit<WeekBar, 'lane'>[] }>();
    const getWeek = (monday: DateTime) => {
      const key = monday.toISODate() || '';
      if (!weeks.has(key)) weeks.set(key, { days: Array.from({ length: 7 }, () => []), segments: [] });
      return weeks.get(key)!;
    };

    myEvents.forEach(ev => {
      const startDay = DateTime.fromISO(ev.start as string).startOf('day');
      const endDay = DateTime.fromISO(ev.end as string).startOf('day');

      if (startDay.hasSame(endDay, 'day')) {
        getWeek(startDay.startOf('week')).days[startDay.weekday - 1].push(ev);
        return;
      }

      // Multi-day: one bar segment per week it touches, clipped to that week's columns
      let monday = startDay.startOf('week');
      while (monday <= endDay) {
        const sunday = monday.plus({ days: 6 });
        getWeek(monday).segments.push({
          ev,
          colStart: startDay < monday ? 0 : startDay.weekday - 1,
          colEnd: endDay > sunday ? 6 : endDay.weekday - 1,
        });
        monday = monday.plus({ weeks: 1 });
      }
    });

    return Array.from(weeks.entries()).map(([key, { days, segments }]) => {
      const monday = DateTime.fromISO(key);

      // Greedy lane packing: each bar goes into the first lane whose days are free
      const lanes: boolean[][] = [];
      const bars: WeekBar[] = [...segments]
        .sort((a, b) => byActualStart(a.ev, b.ev) || (b.colEnd - b.colStart) - (a.colEnd - a.colStart))
        .map(seg => {
          let lane = lanes.findIndex(used => !used.slice(seg.colStart, seg.colEnd + 1).some(Boolean));
          if (lane === -1) {
            lanes.push(Array(7).fill(false));
            lane = lanes.length - 1;
          }
          for (let c = seg.colStart; c <= seg.colEnd; c++) lanes[lane][c] = true;
          return { ...seg, lane };
        });

      return {
        id: `week-${key}`,
        start: iso(monday),
        end: iso(monday.plus({ days: 6 }).endOf('day')),
        days: days.map(items => [...items].sort(byActualStart)),
        bars,
        laneCount: lanes.length,
      };
    });
  }, [myEvents, location]);

  const eventCountByDay = useMemo(() => {
    const counts: Record<string, number> = {};

    myEvents.forEach(ev => {
      const start = DateTime.fromISO(ev.start as string).startOf('day');
      const end = DateTime.fromISO(ev.end as string).startOf('day');

      let cursor = start;
      while (cursor <= end) {
        const key = cursor.toISODate() || ""; // yyyy-MM-dd
        counts[key] = (counts[key] || 0) + 1;
        cursor = cursor.plus({ days: 1 });
      }
    });

    return counts;
  }, [myEvents]);

  // fixed size + icons moved to bottom row next to time
  const renderWpCard = (realEvent: CalendarEvent) => {
    const packet = realEvent.packet;
    const td = packet?.task_details;

    const bg = realEvent.color as string | undefined;
    const textColor = getTextColorForBg(bg);
    const startTime = formatTimeOnly(realEvent.originalStartText);
    const endTime = formatTimeOnly(realEvent.originalEndText);

    const assignedEmployees = td?.assigned_employees_on_calendar;
    const partAmounts = td?.part_amounts ?? 0;
    const priorityBadge = getPriorityBadge(packet?.task_priority);

    const iconStyle: React.CSSProperties = { fontSize: 16, color: textColor, flexShrink: 0 };

    return (
      <div
        key={realEvent.id}
        role="button"
        tabIndex={0}
        onClick={() => openWorkpackage(realEvent)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            // Keep Mobiscroll's own event keyboard handling from also reacting
            e.stopPropagation();
            openWorkpackage(realEvent);
          }
        }}
        title={`${realEvent.description ?? ''}\nDue: ${formatDueDate(packet?.due_date)}`}
        style={{
          minHeight: 48,
          boxSizing: 'border-box',
          padding: '4px 6px',
          borderRadius: 8,
          backgroundColor: bg ?? 'rgba(52,152,219,0.25)',
          color: textColor,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          overflow: 'hidden',
          borderTop: '2px ridge rgba(255,255,255,0.35)',
          cursor: 'pointer',
        }}
      >
        <div
          className="text-[18px] font-medium w-full min-w-0 whitespace-normal break-words [overflow-wrap:anywhere] leading-5"
        >
          {realEvent.title}
        </div>

        <div
          className="w-full flex items-center justify-between gap-2 overflow-hidden"
        >
          <div
            className="text-[18px] opacity-100 whitespace-nowrap text-ellipsis overflow-hidden min-w-0"
          >
            {startTime !== '—' && endTime !== '—' ? `${startTime} → ${endTime}` : ''}
          </div>

          <div className="flex items-center gap-1.5 shrink-0 whitespace-nowrap">
            {!!assignedEmployees && assignedEmployees.length > 0 && (
              <Tooltip title={assignedEmployees} type="primary" placement="top-start">
                <PersonIcon style={iconStyle} />
              </Tooltip>
            )}

            {partAmounts > 0 && (
              <Tooltip title={`${partAmounts} parts`} type="primary" placement="top-start">
                <Badge
                  badgeContent={partAmounts}
                  color="primary"
                  badgeStyle={{ fontSize: 9, height: 14, minWidth: 14 }}
                >
                  <ShoppingCartIcon className="text-base" color={td?.part_not_available ? 'primary' : 'error'} />
                </Badge>
              </Tooltip>
            )}

            {td?.is_messages && (
              <Tooltip title="Message" type="primary" placement="top-start">
                <NotificationsActiveIcon style={{ fontSize: 16, color: td?.is_message_unseen ? '#ff1744' : textColor }} />
              </Tooltip>
            )}

            {td?.is_task_critical && (
              <Tooltip title="Critical Work" type="primary" placement="top-start">
                <PriorityHighIcon className="text-base" color="error" />
              </Tooltip>
            )}

            {td?.is_on_hold && (
              <Tooltip title="On hold" type="primary" placement="top-start">
                <HourglassFullIcon className="text-base" color={td?.status === 'CANCEL' ? 'inherit' : 'error'} />
              </Tooltip>
            )}

            {td?.is_on_time === false && (
              <Tooltip title="Aircraft is not on time or data unavailable, Reschedule" type="primary" placement="top-start">
                <LocalAirportIcon className="text-base" color={td?.status === 'CANCEL' ? 'inherit' : 'error'} />
              </Tooltip>
            )}

            {priorityBadge && (
              <div
                className="w-[18px] h-[18px] bg-[red] text-white font-extrabold text-[10px] flex items-center justify-center rounded-full shrink-0"
              >
                {priorityBadge}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const view = useMemo(
    (): MbscEventcalendarView => ({
      timeline: {
        type: 'week',
        eventHeight: 'variable',
        startTime: '00:00',
        endTime: '24:00',
        timeCellStep: 1440,
        timeLabelStep: 1440,
        size: 1,
        weekNumbers: true,
        virtualScroll: false,
      },
    }),
    []
  );

  return (
    <div className="flex flex-wrap" style={fullScreen ? {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1300,
      backgroundColor: 'white',
      padding: '16px',
      overflow: 'auto'
    } : {}}>
      <div className="w-full">
        <Eventcalendar
          theme="material"
          themeVariant="light"
          firstDay={1}
          showControls={true}
          currentDate={activeDate}
          onPageChange={(ev: MbscPageChangeEvent) => {
            if (ev.firstDay) {
              setActiveDate(ev.firstDay);
            }
          }}
          data={location === 'KEF' ? dayBuckets : nonKefData}
          view={view}
          dragToCreate={false}
          dragToMove={false}
          dragToResize={false}
          clickToCreate={false}
          renderDay={(args) => {
            const day = DateTime.fromJSDate(args.date);
            const key = day.toISODate() || "";
            const count = eventCountByDay[key] || 0;

            return (
              <div
                className="flex items-center justify-center gap-1.5 h-8 whitespace-nowrap"
              >
                {/* Day label */}
                <span className="font-semibold">
                  {day.toFormat('ccc dd')}
                </span>

                <span
                  className="min-w-[16px] h-4 inline-flex items-center justify-center"
                >
                  {count > 0 && (
                    <span
                      className="min-w-[16px] h-4 px-[5px] py-0 rounded-lg text-[11px] font-semibold bg-accent-blue text-white leading-4 text-center"
                    >
                      {count}
                    </span>
                  )}
                </span>
              </div>
            );
          }}

          renderHeader={() => (
            <div
              className="h-5 flex items-center gap-2 py-1 px-2"
            >
              {/* Left: built-in controls (keep navigation & month select alive) */}
              <div className="flex items-center gap-2">
                <CalendarPrev />
                <CalendarToday />
                <CalendarNext />
                <CalendarNav />
              </div>

              {/* Right: Fullscreen toggle and Day/Night toggle */}
              <div className="ml-auto flex items-center gap-2">
                <IconButton size="small" onClick={() => setFullScreen(!fullScreen)}>
                  {!fullScreen ? <FullscreenIcon /> : <FullscreenExitIcon />}
                </IconButton>
                <span>Night</span>
                <Switch checked={isDayNight} onChange={onDayNightToggle} color="secondary" />
                <span>Day</span>
              </div>
            </div>
          )}
          renderScheduleEvent={(args: MbscCalendarEventData) => {
            const id = String(args.id);

            // KEF: one bucket per day, stack that day's work packages with an even gap
            if (id.startsWith('day-')) {
              const bucket = dayBuckets.find(b => b.id === id);
              if (!bucket) return null;

              return (
                <div className="flex flex-col gap-1 px-1 py-1">
                  {bucket.items.map(renderWpCard)}
                </div>
              );
            }

            // Non-KEF: one bucket per week. Bars sit in lane rows at the top; each day's
            // stack starts right under the lowest bar in its column and spans into the
            // trailing 1fr row, so its height doesn't stretch the bar lanes.
            const bucket = nonKefData.find(b => b.id === id);
            if (!bucket) return null;

            const lastLaneByCol = Array.from({ length: 7 }, (_, c) =>
              Math.max(-1, ...bucket.bars.filter(bar => bar.colStart <= c && c <= bar.colEnd).map(bar => bar.lane))
            );

            return (
              <div className="grid grid-cols-7" style={{ gridTemplateRows: bucket.laneCount > 0 ? `repeat(${bucket.laneCount}, auto) 1fr` : '1fr' }}>
                {bucket.bars.map(bar => (
                  <div
                    key={`bar-${bar.ev.id}`}
                    className="px-1 pt-1 min-w-0"
                    style={{ gridColumn: `${bar.colStart + 1} / ${bar.colEnd + 2}`, gridRow: bar.lane + 1 }}
                  >
                    {renderWpCard(bar.ev)}
                  </div>
                ))}
                {bucket.days.map((items, c) => (
                  <div
                    key={`day-${c}`}
                    className="flex flex-col gap-1 px-1 py-1 min-w-0"
                    style={{ gridColumn: c + 1, gridRow: `${lastLaneByCol[c] + 2} / -1` }}
                  >
                    {items.map(renderWpCard)}
                  </div>
                ))}
              </div>
            );
          }}
        />

        <Dialog
          visible={eventDialogOpen}
          max_width="md"
          title={selectedEvent?.wo_name ?? ''}
          context={
            <div>
              <Tabs variant="fullWidth" tabNames={tabNames} tabValue={tabValue} onChangeTabValue={(v: number) => setTabValue(v)} />

              {tabValue === 0 && (
                <div>
                  <LineWorkpackageCard task={selectedEvent} type="LINE" onOpenDialogClicked={() => { }} />
                  <Typography align="center" variant="h2" text={`Tasks(${workpackageTasks.length})`} margin={[1, 0, 0, 0]} />
                  <div>
                    {workpackageTasks.map((task, i) => (
                      // Task card. Previously every line was its own centred
                      // Typography at a different size (Line no left/h3, name
                      // h3, Status h3, dates h4) with bottom margins jumping
                      // 4 → 4 → 1 → 1 → 1, so nothing lined up. Now: one left
                      // alignment, the task name as the title, and the metadata
                      // as label/value pairs on a single type step.
                      <div key={i} className="mb-2">
                        <Card>
                          <CardContent>
                            <div className="flex items-start justify-between gap-2">
                              <Typography text={`${task.name}`} variant="subtitle1" fontWeight={600} align="left" />
                              <span className="ds-chip shrink-0" style={getStatusChipStyle(task.status)}>{task.status}</span>
                            </div>

                            <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                              <span className="ds-muted text-xs">Line no</span>
                              <span className="text-xs">{task.wo_line_id}</span>
                              <span className="ds-muted text-xs">Start date</span>
                              <span className="text-xs">{task.start_date}</span>
                              <span className="ds-muted text-xs">Due date</span>
                              <span className="text-xs">{task.due_date}</span>
                            </div>

                            <div className="mt-2 flex flex-row flex-nowrap gap-1 items-center">
                              <IconButton size="small" onClick={() => redirectToMxi(task.barcode)} className="text-[10px] px-1 w-auto">MXI</IconButton>
                              {(task.status !== "COMPLETE" && !isBoeingAircraft(selectedEvent.inventory)) && (
                                <IconButton size="small" onClick={() => redirectToIdmrTask(task.wo_barcode, task.barcode)} className="text-[10px] px-1 w-auto">eSign</IconButton>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {tabValue === 1 && <LineWorkpackageNotes isEditMode={isAdmin} task={selectedEvent} />}
              {tabValue === 2 && isLinePlanner && (
                <div>
                  <Typography text={selectedEvent?.tail_number} align="center" variant="h2" margin={[1, 0, 1, 0]} />
                  <Table
                    headers={['Arrival', 'Departure']}
                    rows={selectedEvent?.aircraft_arrival_and_departure_times || []}
                    generateBody={(row: DateRanges, index: number) => generateTableBody(row, index)}
                    defaultRowsPerPage={25}
                  />
                </div>
              )}
            </div>
          }
          onClose={() => onClose()}
          strict={false}
          show_options={false}
        />
      </div>
    </div>
  );
};
export default WorkPackageCalendar;